import { useState } from "react";
import { toast } from "sonner";
import { Droplet, Loader2, CheckCircle2, CalendarDays } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const OLIVE_DATE = "Sabato 3 ottobre 2026";

export const OliveAvailabilityForm = ({ variant = "light" }) => {
  const [form, setForm] = useState({ name: "", contact: "", codice_fiscale: "", dietary_needs: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) { toast.error("Inserisci nome e contatto."); return; }
    if (form.codice_fiscale.trim().length < 11) { toast.error("Inserisci un codice fiscale valido (obbligatorio per l'assicurazione)."); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/olive-availability`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(), contact: form.contact.trim(),
          codice_fiscale: form.codice_fiscale.trim().toUpperCase(),
          dietary_needs: form.dietary_needs.trim() || null,
          days: [OLIVE_DATE], message: form.message.trim() || null,
        }),
      });
      if (!r.ok) throw new Error("Errore nell'invio.");
      setDone(true);
    } catch (err) { toast.error(err.message || "Riprova."); } finally { setLoading(false); }
  };

  return (
    <div className={`rounded-3xl p-6 sm:p-8 ${variant === "light" ? "bg-[#FBF9F6] text-[#1A1A1A] border-2 border-[#E5E1D8]" : "bg-white text-[#1A1A1A]"}`}>
      {done ? (
        <div data-testid="olio-success" className="text-center py-8">
          <CheckCircle2 className="w-14 h-14 text-[#2E4F3B] mx-auto mb-4" />
          <h3 className="font-display text-2xl font-black">Grazie!</h3>
          <p className="mt-3 text-[#5C5C5C] leading-relaxed">Abbiamo registrato la tua disponibilità. Ti contatteremo appena date e dettagli saranno confermati.</p>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="olio-form" className="space-y-4">
          <div className="flex items-center gap-2">
            <Droplet className="w-5 h-5 text-[#C15C3D]" />
            <h3 className="font-display text-xl sm:text-2xl font-black">Dai la tua disponibilità</h3>
          </div>
          <p className="text-sm text-[#5C5C5C] leading-relaxed">
            È una raccolta preliminare di disponibilità: date, orari e dettagli saranno confermati.
          </p>
          <input data-testid="olio-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome e cognome *" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B]" />
          <input data-testid="olio-contact" value={form.contact} onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))} placeholder="Telefono o email *" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B]" />
          <div>
            <input data-testid="olio-cf" value={form.codice_fiscale} onChange={(e) => setForm((f) => ({ ...f, codice_fiscale: e.target.value.toUpperCase() }))} maxLength={16} placeholder="Codice fiscale *" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm uppercase tracking-wide focus:outline-none focus:border-[#2E4F3B]" />
            <p className="mt-1 text-[11px] text-[#5C5C5C]">Obbligatorio per l'assicurazione dei volontari.</p>
          </div>
          <textarea data-testid="olio-dietary" rows={2} value={form.dietary_needs} onChange={(e) => setForm((f) => ({ ...f, dietary_needs: e.target.value }))} placeholder="Esigenze per il pranzo? (es. vegano, vegetariano, allergie/intolleranze)" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B] resize-none" />
          <div className="flex items-center gap-2 rounded-xl bg-[#2E4F3B]/8 border border-[#2E4F3B]/20 px-4 py-3">
            <CalendarDays className="w-4 h-4 text-[#2E4F3B] shrink-0" />
            <span className="text-sm font-semibold text-[#1A1A1A]">Giornata: {OLIVE_DATE} <span className="font-normal text-[#5C5C5C]">· data da confermare</span></span>
          </div>
          <textarea data-testid="olio-message" rows={2} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Note (facoltativo)" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B] resize-none" />
          <button type="submit" disabled={loading} data-testid="olio-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] disabled:opacity-60 text-white font-bold px-6 py-4 transition-colors">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Droplet className="w-5 h-5" />}
            Dai la tua disponibilità
          </button>
        </form>
      )}
    </div>
  );
};
